import React from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import moment from 'moment'
import {Trans} from '@modifi/plugin-i18n'
import {Link} from '@modifi/router'
import {ButtonLink, SkeletonLine} from '@modifi/ui'
import {clientConfig} from '@modifi/client-config'

import {Event, EventText, StrongText as Strong, Timeline} from './styles'
import {
  first,
  injectTransitEventsToContainerEvents,
  isTransitEvent,
  last,
} from '../../../utils/container-events'

const marineTrafficUrl = clientConfig.get('SERVICE_MARINE_TRAFFIC', false) as string | false

const transComponents = {
  strong: <Strong />,
}

const propTypes = {
  containerEvents: PropTypes.arrayOf(
    PropTypes.shape({
      location: PropTypes.shape({
        name: PropTypes.string,
        countryCode: PropTypes.string,
      }),
      destination: PropTypes.shape({
        name: PropTypes.string,
        countryCode: PropTypes.string,
      }),
      events: PropTypes.arrayOf(
        PropTypes.shape({
          code: PropTypes.string,
          date: PropTypes.string,
          isEstimated: PropTypes.bool,
          vesselNumber: PropTypes.string,
        }).isRequired
      ),
    }).isRequired
  ),
  t: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
}
const defaultProps = {
  containerEvents: undefined,
}

type EventTimelineProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const EventTimeline: React.FC<EventTimelineProps> = ({containerEvents, isLoading, t}) => {
  const extendedContainerEvents = injectTransitEventsToContainerEvents(containerEvents ?? [])

  if (isLoading) {
    return (
      <Timeline>
        <Event>
          <EventText>
            <Strong>
              <SkeletonLine stringLength={25} />
            </Strong>
          </EventText>
          <EventText ml={10} mt={5}>
            <SkeletonLine stringLength={10} /> - <SkeletonLine stringLength={25} />
          </EventText>
          <EventText ml={10} mt={5}>
            <SkeletonLine stringLength={10} /> - <SkeletonLine stringLength={25} />
          </EventText>
        </Event>
        <Event>
          <EventText>
            <SkeletonLine stringLength={36} />
          </EventText>
          <EventText ml={10} mt={5}>
            <SkeletonLine stringLength={10} /> - <SkeletonLine stringLength={10} />
          </EventText>
        </Event>
        <Event>
          <EventText>
            <Strong>
              <SkeletonLine stringLength={25} />
            </Strong>
          </EventText>
          <EventText ml={10} mt={5}>
            <SkeletonLine stringLength={10} /> - <SkeletonLine stringLength={25} />
          </EventText>
          <EventText ml={10} mt={5}>
            <SkeletonLine stringLength={10} /> - <SkeletonLine stringLength={25} />
          </EventText>
        </Event>
      </Timeline>
    )
  }

  return (
    <Timeline>
      {extendedContainerEvents?.map((containerEvent, index) => {
        if (isTransitEvent(containerEvent)) {
          return (
            <Event
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              $current={moment(containerEvent?.departureDate).isSameOrBefore()}
              $done={moment(containerEvent?.arrivalDate).isSameOrBefore()}
            >
              <EventText>
                <Trans
                  t={t}
                  i18nKey={`container-events-transit.label${
                    containerEvent?.isEstimated ? '-estimated' : ''
                  }`}
                  values={{
                    departureLocationName: containerEvent?.departureLocation?.name,
                    departureLocationCountryCode: containerEvent?.departureLocation?.countryCode,
                    arrivalLocationName: containerEvent?.arrivalLocation?.name,
                    arrivalLocationCountryCode: containerEvent?.arrivalLocation?.countryCode,
                    count: moment(containerEvent?.departureDate).diff(
                      containerEvent?.arrivalDate,
                      'days'
                    ),
                  }}
                  components={transComponents}
                />
              </EventText>

              {containerEvent?.vesselNumber && marineTrafficUrl && (
                <Link
                  to={`${marineTrafficUrl}/en/ais/details/ships/imo:${containerEvent?.vesselNumber}`}
                  isExternal
                  targetBlank
                >
                  <ButtonLink transparent data-target="marine-traffic-link">
                    <span>
                      {t('container-events-vessel.link', {number: containerEvent?.vesselNumber})}
                    </span>
                  </ButtonLink>
                </Link>
              )}

              <EventText ml={10} mt={5}>
                <Trans
                  t={t}
                  i18nKey={`container-events-transit.text${
                    containerEvent?.isEstimated ? '-estimated' : ''
                  }`}
                  values={containerEvent ?? {}}
                />
              </EventText>
            </Event>
          )
        }

        return (
          <Event
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            $current={moment(first(containerEvent?.events ?? [])?.date).isSameOrBefore()}
            $done={moment(last(containerEvent?.events ?? [])?.date).isSameOrBefore()}
          >
            {(index === 0 || index === extendedContainerEvents.length - 1) && (
              <EventText>
                <Trans
                  t={t}
                  i18nKey="container-events.label"
                  components={transComponents}
                  values={{
                    locationName: containerEvent?.location?.name,
                    locationCountryCode: containerEvent?.location?.countryCode,
                  }}
                />
              </EventText>
            )}

            {containerEvent?.events?.map((item, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <EventText ml={10} mt={5} key={`${item?.code}_${i}`}>
                <Trans
                  t={t}
                  i18nKey={`container-events.text${item?.isEstimated ? '-estimated' : ''}`}
                  values={item}
                  components={transComponents}
                />
              </EventText>
            ))}
          </Event>
        )
      })}
    </Timeline>
  )
}

EventTimeline.propTypes = propTypes
EventTimeline.defaultProps = defaultProps

export default EventTimeline
